import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGIN_URL = `${window.location.origin}/parent/login`;

// Invite pages are unauthenticated — use a dedicated axios instance so we
// never strip or mutate the owner's global Authorization header.
const inviteAxios = axios.create({ baseURL: API });

export default function ParentInviteAcceptPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null); // null or response object

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await inviteAxios.get(`/parent-communication/invite/${token}`);
        setInvite(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Invite not found");
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token]);

  const handleAcceptInvite = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const res = await inviteAxios.post(`/parent-communication/invite/${token}/accept`, { password, confirmPassword });
      setAccepted(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="parent-invite-loading-state">Loading invite…</div>;
  }

  if (!invite) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="parent-invite-empty-state">This invite is unavailable.</div>;
  }

  // ── Success: account just created ────────────────────────────────────────
  if (accepted) {
    const loginIdentifier = accepted.loginIdentifier || accepted.parentEmail || "";
    const isPhone = loginIdentifier && !loginIdentifier.includes("@");
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(249,115,22,0.16),_transparent_34%),#f8fbfd] px-4 py-10 flex items-center justify-center" data-testid="parent-invite-success-page">
        <Card className="w-full max-w-md border-0 bg-white/92 shadow-2xl ring-1 ring-slate-200/80">
          <CardContent className="space-y-5 p-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <svg className="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900" data-testid="parent-invite-success-title">Account created!</h2>
              <p className="mt-2 text-sm text-slate-600">Your parent portal account is ready. Use the button below to log in now — and bookmark the link so you can come back anytime.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 text-left space-y-1">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{isPhone ? "Your login phone number" : "Your login email"}</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="parent-invite-success-email">{loginIdentifier}</p>
            </div>
            <Button
              onClick={() => navigate("/parent/login", { state: { identifier: loginIdentifier } })}
              className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="parent-invite-go-to-login-btn"
            >
              Go to Parent Portal Login
            </Button>
            <p className="text-xs text-slate-400">
              Bookmark this to return anytime:<br />
              <a href={LOGIN_URL} className="text-emerald-600 font-medium break-all" data-testid="parent-invite-login-url">{LOGIN_URL}</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Already used: parent returning via old invite link ───────────────────
  if (invite.inviteStatus !== "pending") {
    const loginIdentifier = invite.email || invite.phone || "";
    const isPhone = loginIdentifier && !loginIdentifier.includes("@");
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(249,115,22,0.16),_transparent_34%),#f8fbfd] px-4 py-10 flex items-center justify-center" data-testid="parent-invite-already-used-page">
        <Card className="w-full max-w-md border-0 bg-white/92 shadow-2xl ring-1 ring-slate-200/80">
          <CardContent className="space-y-5 p-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
              <svg className="h-7 w-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900" data-testid="parent-invite-used-title">Account already set up</h2>
              <p className="mt-2 text-sm text-slate-600">This invite link has already been used. Your account is active — log in below with your {isPhone ? "phone number" : "email"} and password.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 text-left space-y-1">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{isPhone ? "Your login phone number" : "Your login email"}</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="parent-invite-used-email">{loginIdentifier}</p>
            </div>
            <Button
              onClick={() => navigate("/parent/login", { state: { identifier: loginIdentifier } })}
              className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="parent-invite-used-login-btn"
            >
              Go to Parent Portal Login
            </Button>
            <p className="text-xs text-slate-400">
              Bookmark this to return anytime:<br />
              <a href={LOGIN_URL} className="text-emerald-600 font-medium break-all" data-testid="parent-invite-used-login-url">{LOGIN_URL}</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Pending: first-time invite acceptance ────────────────────────────────
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(249,115,22,0.16),_transparent_34%),#f8fbfd] px-4 py-10" data-testid="parent-invite-page">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <section
          className="relative overflow-hidden rounded-[2.2rem] shadow-2xl"
          style={{ background: "linear-gradient(135deg, #0b4f4c 0%, #0f6b82 55%, #c96c1a 100%)" }}
          data-testid="parent-invite-hero"
        >
          <img
            src={invite.heroImageUrl}
            alt=""
            aria-hidden="true"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity"
            data-testid="parent-invite-hero-image"
          />
          <div className="relative space-y-5 px-6 py-8 text-white lg:px-10 lg:py-12">
            <img src="/simple-sign-in-logo.png" alt="ToDays Kidz logo" className="h-14 w-14 rounded-2xl bg-white/95 p-2" data-testid="parent-invite-logo" />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/80" data-testid="parent-invite-eyebrow">{invite.centerName}</p>
              <h1 className="text-4xl font-bold leading-tight sm:text-5xl" data-testid="parent-invite-title">You've been invited to the parent portal.</h1>
              <p className="mt-3 max-w-2xl text-sm text-white/90 sm:text-base" data-testid="parent-invite-description">Create your password to begin your {invite.trialDays}-day trial. If the trial ends, you can still log in and view the upgrade/signup screen.</p>
            </div>
          </div>
        </section>

        <Card className="border-0 bg-white/92 shadow-2xl ring-1 ring-slate-200/80" data-testid="parent-invite-card">
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div className="rounded-[1.6rem] bg-slate-50 p-4" data-testid="parent-invite-summary-card">
              <p className="font-semibold text-slate-900" data-testid="parent-invite-parent-name">{invite.parentName}</p>
              {invite.email ? (
                <p className="mt-1 text-sm text-slate-600" data-testid="parent-invite-email">{invite.email}</p>
              ) : invite.phone ? (
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-slate-600" data-testid="parent-invite-phone">{invite.phone}</p>
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 ring-1 ring-amber-200">
                    Your phone number is being used because no email address is on file. Contact your center director if you'd like to add one.
                  </p>
                </div>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2" data-testid="parent-invite-child-list">
                {invite.childNames.map((childName) => (
                  <span key={childName} className="rounded-full bg-white px-3 py-1 text-sm ring-1 ring-slate-200">{childName}</span>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 border-2 border-amber-400 px-4 py-4 text-sm text-amber-900" data-testid="parent-invite-login-note">
              <p className="font-extrabold text-base text-amber-800 mb-1">Important — You must use this to log in:</p>
              <p className="text-lg font-bold text-slate-900 tracking-tight break-all" data-testid="parent-invite-login-identifier">
                {invite.email || invite.phone}
              </p>
              <p className="mt-2 text-xs text-amber-700">Save this somewhere safe. You will need it every time you return to the parent portal.</p>
            </div>

            <form className="space-y-4" onSubmit={handleAcceptInvite} data-testid="parent-invite-accept-form">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-900" htmlFor="invite-password">Create password</label>
                <Input id="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="parent-invite-password-input" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-900" htmlFor="invite-confirm-password">Confirm password</label>
                <Input id="invite-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="parent-invite-confirm-password-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="parent-invite-submit-button">
                {submitting ? "Creating account..." : "Create parent account"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
